/**
 * Safe Logger Utility
 * Prevents Safari Inspector crashes by safely logging objects
 * and avoiding circular references
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const enableLogging = process.env.REACT_APP_ENABLE_LOGGING !== 'false';

// Check if Safari is being used
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// Check if DevTools is open (approximation)
let devToolsOpen = false;
const threshold = 160;
const devtools = { open: false };

// Only check for DevTools in Safari
if (isSafari) {
  setInterval(() => {
    if (window.outerHeight - window.innerHeight > threshold ||
        window.outerWidth - window.innerWidth > threshold) {
      if (!devtools.open) {
        devtools.open = true;
        devToolsOpen = true;
        console.warn('🔧 Safari DevTools detected - Switching to safe logging mode');
      }
    } else {
      if (devtools.open) {
        devtools.open = false;
        devToolsOpen = false;
        console.info('✅ Safari DevTools closed - Normal logging resumed');
      }
    }
  }, 500);
}

/**
 * Safely stringify objects, handling circular references
 */
function safeStringify(obj, indent = 2) {
  const seen = new WeakSet();

  return JSON.stringify(obj, (key, value) => {
    // Skip functions
    if (typeof value === 'function') {
      return '[Function]';
    }

    // Handle circular references
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);

      // Limit object depth for Safari
      if (isSafari && devToolsOpen) {
        const depth = getObjectDepth(value);
        if (depth > 3) {
          return '[Deep Object - Truncated]';
        }
      }
    }

    return value;
  }, indent);
}

/**
 * Get object depth
 */
function getObjectDepth(obj, currentDepth = 0, maxDepth = 10) {
  if (currentDepth >= maxDepth) return maxDepth;

  if (typeof obj !== 'object' || obj === null) {
    return currentDepth;
  }

  let maxChildDepth = currentDepth;

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const childDepth = getObjectDepth(obj[key], currentDepth + 1, maxDepth);
      maxChildDepth = Math.max(maxChildDepth, childDepth);
    }
  }

  return maxChildDepth;
}

/**
 * Safe console.log replacement
 */
export const safeLog = (...args) => {
  if (!enableLogging || !isDevelopment) return;

  // If Safari with DevTools open, use safe logging
  if (isSafari && devToolsOpen) {
    const safeArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        try {
          // For small objects, try normal stringify first
          const str = safeStringify(arg);
          if (str.length < 1000) {
            return str;
          }
          // For large objects, just show keys
          return `[Object with keys: ${Object.keys(arg).slice(0, 5).join(', ')}...]`;
        } catch (e) {
          return '[Object - Cannot Display]';
        }
      }
      return arg;
    });

    console.log(...safeArgs);
  } else {
    // Normal logging for other browsers
    console.log(...args);
  }
};

/**
 * Safe console.error replacement
 */
export const safeError = (...args) => {
  if (!enableLogging) return;

  if (isSafari && devToolsOpen) {
    const safeArgs = args.map(arg => {
      if (arg instanceof Error) {
        return `[Error: ${arg.message}]`;
      }
      if (typeof arg === 'object' && arg !== null) {
        try {
          return safeStringify(arg);
        } catch {
          return '[Object - Cannot Display]';
        }
      }
      return arg;
    });

    console.error(...safeArgs);
  } else {
    console.error(...args);
  }
};

/**
 * Safe console.warn replacement
 */
export const safeWarn = (...args) => {
  if (!enableLogging) return;

  if (isSafari && devToolsOpen) {
    const safeArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        try {
          return safeStringify(arg);
        } catch {
          return '[Object - Cannot Display]';
        }
      }
      return arg;
    });

    console.warn(...safeArgs);
  } else {
    console.warn(...args);
  }
};

/**
 * Create a debug logger for a specific module
 */
export const createLogger = (moduleName) => ({
  log: (...args) => safeLog(`[${moduleName}]`, ...args),
  error: (...args) => safeError(`[${moduleName}]`, ...args),
  warn: (...args) => safeWarn(`[${moduleName}]`, ...args),
  debug: (...args) => {
    if (process.env.REACT_APP_DEBUG === 'true') {
      safeLog(`[${moduleName}:DEBUG]`, ...args);
    }
  }
});

// Export default logger
export default {
  log: safeLog,
  error: safeError,
  warn: safeWarn,
  debug: (...args) => {
    if (process.env.REACT_APP_DEBUG === 'true') {
      safeLog('[DEBUG]', ...args);
    }
  }
};